import { z } from 'zod';
import type { SocialAuthProvider } from '@/components/ui/SocialAuthButton';

export const SOCIAL_AUTH_STORAGE_NAME = 'creator_social_auth';
const schema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('invite'),
    provider: z.enum(['Facebook', 'Google', 'Apple']),
    magicToken: z.string().trim().min(1),
    destination: z.literal('invite-proposal'),
    phase: z.enum(['redirecting', 'authenticated', 'linked', 'failed']),
  }),
  z.object({ kind: z.literal('direct'), provider: z.enum(['Facebook', 'Google', 'Apple']) }),
]);

export type SocialAuthContext = z.infer<typeof schema>;

export function storeSocialAuthContext(context: SocialAuthContext) {
  sessionStorage.setItem(SOCIAL_AUTH_STORAGE_NAME, JSON.stringify(schema.parse(context)));
}

export function readSocialAuthContext(): SocialAuthContext | null {
  try {
    const raw = sessionStorage.getItem(SOCIAL_AUTH_STORAGE_NAME);
    if (!raw) return null;
    const result = schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function hasResumableSocialContext() {
  const context = readSocialAuthContext();
  return (
    context !== null &&
    (context.kind === 'direct' || context.phase === 'authenticated' || context.phase === 'linked')
  );
}

export function clearSocialAuthContext() {
  try {
    sessionStorage.removeItem(SOCIAL_AUTH_STORAGE_NAME);
  } catch {
    // Storage cleanup must not turn successful authentication into a failure.
  }
}

export function storeInviteSocialAuthContext(magicToken: string, provider: SocialAuthProvider) {
  storeSocialAuthContext({
    kind: 'invite',
    provider,
    magicToken,
    destination: 'invite-proposal',
    phase: 'redirecting',
  });
}
